import { authenticatedFetch } from './authApi';
import { getEnv } from '../utils/env';
import { getCachedOrFetch, invalidateCachedValue } from './requestCache';
import { normalizePositionPreferences } from '../utils/player';
import { encryptValue, looksEncrypted } from '../utils/encryption';
import { choosePlayerNameMatch, type PlayerNameMatch } from '@waedo/frontend-core/player-name-check';

type UnknownRecord = Record<string, unknown>;

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
const PLAYERS_LIST_CACHE_PREFIX = 'players:list:';
const PLAYERS_LIST_TTL_MS = 10_000;

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && typeof (payload as UnknownRecord).detail === 'string') {
    return (payload as UnknownRecord).detail as string;
  }
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const error = (payload as UnknownRecord).error;
    if (typeof error === 'string' && error.trim().length > 0) return error;
    if (error && typeof error === 'object' && 'message' in (error as UnknownRecord)) {
      const message = (error as UnknownRecord).message;
      if (typeof message === 'string' && message.trim().length > 0) return message;
    }
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const serializePlayerPayload = async (payload: Record<string, unknown>): Promise<Record<string, unknown>> => {
  const nextPayload: Record<string, unknown> = { ...payload };

  const clubId = typeof nextPayload.club_id === 'string' ? nextPayload.club_id : '';
  const rawName = typeof nextPayload.name === 'string' ? nextPayload.name : '';

  if (clubId && rawName && !looksEncrypted(rawName)) {
    try {
      nextPayload.name = await encryptValue({
        plaintext: rawName,
        clubId,
      });
    } catch {
      // Fall back to the original value so player creation/update itself does not fail on crypto issues.
    }
  }

  if ('positions' in nextPayload) {
    const normalized = normalizePositionPreferences(nextPayload.positions);
    // Persist both variants; the active preference list loses the other variant.
    nextPayload.positions = normalized.variants;
  }

  return nextPayload;
};

const playersRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, options);

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractMessage(payload, `Players API request failed (${response.status})`));
  }

  return extractData(payload);
};

export type PlayerMergeAudit = {
  source_id: string; target_id: string; checked_at: string; complete: boolean; merge_ready: false;
  checks: { key: string; status: 'checked' | 'unavailable'; source_count: number | null; target_count: number | null; overlap_count: number | null }[];
};

export const checkPlayerMergeApi = async (clubId: string, sourceId: string, targetId: string, signal?: AbortSignal): Promise<PlayerMergeAudit> => {
  return await playersRequest('/api/v1/players/merge-audit', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, signal,
    body: JSON.stringify({ club_id: clubId, source_id: sourceId, target_id: targetId }),
  }) as PlayerMergeAudit;
};

export type PlayerMergePlan = {
  revision: string; source_id: string; target_id: string; changed_records: number;
  statistics_refresh: 'next_batch';
  excluded_matches: { match_id: string; reason: string }[];
};
export type PlayerMergeResult = { merged: true; source_id: string; target_id: string; backup_id: string; statistics_refresh: 'next_batch'; excluded_matches: { match_id: string; reason: string }[] };

export const planPlayerMergeApi = async (clubId: string, sourceId: string, targetId: string, signal?: AbortSignal): Promise<PlayerMergePlan> =>
  await playersRequest('/api/v1/players/merge-plan', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, signal,
    body: JSON.stringify({ club_id: clubId, source_id: sourceId, target_id: targetId }),
  }) as PlayerMergePlan;

export const executePlayerMergeApi = async (clubId: string, plan: PlayerMergePlan): Promise<PlayerMergeResult> => {
  const result = await playersRequest('/api/v1/players/merge', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ club_id: clubId, source_id: plan.source_id, target_id: plan.target_id, revision: plan.revision, confirmed: true }),
  }) as PlayerMergeResult;
  for (const prefix of ['players:', 'teams:', 'dashboard:', 'matches:']) invalidateCachedValue(prefix);
  return result;
};

export const createPlayerApi = async (payload: Record<string, unknown>, context?: { teamId?: string; standIn?: boolean }): Promise<unknown> => {
  if (context?.standIn && !context.teamId?.trim()) throw new Error('Match team is required to create a stand-in');
  const matches = await checkPlayerNameApi(String(payload.club_id || ''), String(payload.name || ''));
  const existingId = await choosePlayerNameMatch(matches, context?.teamId, context?.standIn);
  if (existingId) {
    const existing = await getPlayerApi(existingId);
    if (!existing || typeof existing !== 'object' || (existing as UnknownRecord).club_id !== payload.club_id) throw new Error('Existing player unavailable');
    return { ...existing, reusedExistingPlayer: true };
  }
  const serializedPayload = await serializePlayerPayload(payload);
  const created = await playersRequest('/api/v1/players', {
    method: 'POST',
    body: JSON.stringify(serializedPayload),
  });
  invalidateCachedValue(PLAYERS_LIST_CACHE_PREFIX);
  return created;
};

export const checkPlayerNameApi = async (clubId: string, name: string): Promise<PlayerNameMatch[]> => {
  const matches = await playersRequest('/api/v1/players/name-matches', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ club_id: clubId, name }),
  }) as PlayerNameMatch[];
  if (!Array.isArray(matches)) throw new Error('Player name check unavailable');
  return matches;
};

const extractList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    if (Array.isArray(record.players)) return record.players as unknown[];
    if (Array.isArray(record.items)) return record.items as unknown[];
    if (Array.isArray(record.results)) return record.results as unknown[];
    if (Array.isArray(record.data)) return record.data as unknown[];
  }
  return [];
};

export const listPlayersApi = async (params?: {
  active?: boolean | null;
  ids?: string[];
  team_id?: string | null;
}): Promise<unknown[]> => {
  const query = new URLSearchParams();
  if (typeof params?.active === 'boolean') query.set('active', String(params.active));
  if (params?.team_id) query.set('team_id', params.team_id);
  if (params?.ids && params.ids.length > 0) {
    query.set(
      'ids',
      params.ids
        .map((id) => String(id).trim())
        .filter((id) => id.length > 0)
        .join(',')
    );
  }
  const qs = query.toString();
  const path = `/api/v1/players${qs ? `?${qs}` : ''}`;
  return await getCachedOrFetch(`${PLAYERS_LIST_CACHE_PREFIX}${qs}`, PLAYERS_LIST_TTL_MS, async () => {
    const payload = await playersRequest(path, {
      method: 'GET',
    });
    return extractList(payload);
  });
};

export const getPlayerApi = async (id: string | number): Promise<unknown | null> => {
  const payload = await playersRequest(`/api/v1/players/${encodeURIComponent(String(id))}`, {
    method: 'GET',
  });
  return payload ?? null;
};

export const updatePlayerApi = async (id: string | number, payload: Record<string, unknown>): Promise<unknown> => {
  const serializedPayload = await serializePlayerPayload(payload);
  const updated = await playersRequest(`/api/v1/players/${encodeURIComponent(String(id))}`, {
    method: 'PATCH',
    body: JSON.stringify(serializedPayload),
  });
  invalidateCachedValue(PLAYERS_LIST_CACHE_PREFIX);
  return updated;
};

export const deletePlayerApi = async (id: string): Promise<void> => {
  await playersRequest(`/api/v1/players/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  invalidateCachedValue(PLAYERS_LIST_CACHE_PREFIX);
};
